import React, { useState, useEffect } from 'react';
import {
  User,
  BookOpen,
  TrendingUp,
  Clock,
  CheckCircle,
  Shield,
  BarChart3,
  PlayCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

const ProfilePage = () => {
  const { user, isAdmin } = useAuth();
  const [userStats, setUserStats] = useState({
    totalModules: 0,
    completedModules: 0,
    totalChapters: 0,
    completedChapters: 0,
    overallProgress: 0,
    totalWatchTime: 0
  });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchUserProfile();
  }, []);

  // Trigger animations after content loads
  useEffect(() => {
    if (!loading && modules.length >= 0) {
      setTimeout(() => setContentLoaded(true), 100);
    }
  }, [loading, modules]);

  const calculateVideoDuration = async (videoFilename) => {
    return new Promise((resolve) => {
      if (!videoFilename) {
        resolve(null);
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      
      video.onloadedmetadata = () => {
        const duration = video.duration;
        if (duration && !isNaN(duration)) {
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          
          resolve({
            seconds: duration,
            minutes: minutes,
            formatted: minutes > 0 ? 
              (seconds > 0 ? `${minutes}min ${seconds}seg` : `${minutes}min`) : 
              `${seconds}seg`
          });
        } else {
          resolve(null);
        }
        
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        console.warn(`Could not load video: ${videoFilename}`);
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
      
      try {
        const videoUrl = apiService.getVideoUrl(videoFilename);
        video.src = videoUrl;
      } catch (error) {
        console.warn(`Error creating video URL for: ${videoFilename}`);
        resolve(null);
      }
    });
  };

  const calculateModuleDurations = async (chapters) => {
    if (!chapters || chapters.length === 0) {
      return { formatted: '0min', minutes: 0, hasVideos: false };
    }

    const videoDurations = await Promise.all(
      chapters.map(async (chapter) => {
        if (chapter.video_filename) {
          const duration = await calculateVideoDuration(chapter.video_filename);
          return duration;
        }
        return null;
      })
    );

    const validDurations = videoDurations.filter(d => d !== null);
    const totalSeconds = validDurations.reduce((sum, d) => sum + d.seconds, 0);
    const totalMinutes = Math.floor(totalSeconds / 60);
    
    let formatted;
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      formatted = remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
    } else {
      formatted = totalMinutes > 0 ? `${totalMinutes}min` : '0min';
    }

    return {
      formatted,
      minutes: totalMinutes,
      hasVideos: validDurations.length > 0
    };
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 ProfilePage: Fetching user profile data...');
      
      const modulesData = await apiService.getModules();
      console.log('📚 ProfilePage: Modules data received:', modulesData);
      
      const modulesWithProgress = await Promise.all(
        modulesData.map(async (module) => {
          try {
            const moduleChapters = await apiService.getChapters(module.id);
            console.log(`📚 ProfilePage: Module ${module.id} chapters:`, moduleChapters);
            
            const durationData = await calculateModuleDurations(moduleChapters);
            
            const totalChapters = moduleChapters.length || 0;
            const completedChapters = moduleChapters.filter(ch => ch.completed).length || 0;
            const progressPercentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
            
            return {
              ...module,
              chapters: moduleChapters,
              total_chapters: totalChapters,
              completed_chapters: completedChapters,
              progress_percentage: progressPercentage,
              total_duration: durationData.formatted,
              total_minutes: durationData.minutes,
              has_videos: durationData.hasVideos
            };
          } catch (chapterError) {
            console.error(`ProfilePage: Error processing module ${module.id}:`, chapterError);
            return {
              ...module,
              chapters: [],
              total_chapters: 0,
              completed_chapters: 0,
              progress_percentage: 0,
              total_duration: '0min',
              total_minutes: 0,
              has_videos: false
            };
          }
        })
      );
      
      setModules(modulesWithProgress);
      
      const totalModules = modulesWithProgress.length;
      const completedModules = modulesWithProgress.filter(module => module.progress_percentage === 100).length;
      
      const totalChapters = modulesWithProgress.reduce((sum, module) => sum + (module.total_chapters || 0), 0);
      const completedChapters = modulesWithProgress.reduce((sum, module) => sum + (module.completed_chapters || 0), 0);
      
      const overallProgress = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
      
      const totalWatchTime = modulesWithProgress.reduce((sum, module) => sum + (module.total_minutes || 0), 0);

      console.log('📊 ProfilePage: Calculated stats with auto durations:', {
        totalModules,
        completedModules,
        totalChapters,
        completedChapters,
        overallProgress,
        totalWatchTime: `${totalWatchTime} minutes`
      });

      setUserStats({
        totalModules,
        completedModules,
        totalChapters,
        completedChapters,
        overallProgress,
        totalWatchTime
      });

    } catch (error) {
      console.error('❌ ProfilePage: Error fetching profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 70) return 'bg-blue-600';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const formatWatchTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  };

  // Animation styles
  const animationStyles = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes slideInLeft {
      from { opacity: 0; transform: translateX(-20px); }
      to { opacity: 1; transform: translateX(0); }
    }
    .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
    .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
    .animate-scaleIn { animation: scaleIn 0.4s ease-out forwards; }
    .animate-slideInLeft { animation: slideInLeft 0.5s ease-out forwards; }
    .stagger-1 { animation-delay: 0.1s; }
    .stagger-2 { animation-delay: 0.2s; }
    .stagger-3 { animation-delay: 0.3s; }
    .stagger-4 { animation-delay: 0.4s; }
  `;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">Cargando perfil...</p>
          <p className="text-xs sm:text-sm text-blue-600 mt-2">Calculando duraciones de videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Error al cargar perfil</h2>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
          <button
            onClick={fetchUserProfile}
            className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 md:py-8">
      <style>{animationStyles}</style>
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Profile Header */}
        <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 md:mb-8 ${contentLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-6">
            {/* User Avatar */}
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-blue-600" />
            </div>

            {/* User Info */}
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {user?.first_name} {user?.last_name}
                {isAdmin && (
                  <span className="ml-2 sm:ml-3 inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-purple-100 text-purple-800">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Admin
                  </span>
                )}
              </h1>
              <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-4 text-gray-600 text-sm sm:text-base">
                <div className="flex items-center">
                  <User className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">@{user?.username}</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span>{userStats.overallProgress}% completado</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
          <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-scaleIn' : 'opacity-0'}`}>
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-blue-100 flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Progreso General</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{userStats.overallProgress}%</p>
              </div>
            </div>
          </div>

          <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-scaleIn stagger-1' : 'opacity-0'}`}>
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-green-100 flex-shrink-0">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Módulos</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">
                  {userStats.completedModules}/{userStats.totalModules}
                </p>
              </div>
            </div>
          </div>

          <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-scaleIn stagger-2' : 'opacity-0'}`}>
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-purple-100 flex-shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Capítulos</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-600">
                  {userStats.completedChapters}/{userStats.totalChapters}
                </p>
              </div>
            </div>
          </div>

          <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-scaleIn stagger-3' : 'opacity-0'}`}>
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-orange-100 flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-orange-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Tiempo Total</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600 truncate">
                  {formatWatchTime(userStats.totalWatchTime)}
                </p>
                <p className="text-xs text-gray-500">Auto-calculado</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Module Progress */}
          <div className="lg:col-span-2">
            <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-fadeInUp stagger-4' : 'opacity-0'}`}>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" />
                <span>Progreso por Módulo</span>
              </h3>
              <div className="space-y-4 sm:space-y-6">
                {modules.length > 0 ? modules.map((module, index) => {
                  const progress = module.progress_percentage || 0;
                  const totalChapters = module.total_chapters || 0;
                  const completedChapters = module.completed_chapters || 0;
                  
                  return (
                    <div 
                      key={module.id} 
                      className="border-b border-gray-100 pb-4 last:border-b-0 animate-slideInLeft"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                        <div className="flex items-center min-w-0 flex-1">
                          <span className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">{module.icon || '📚'}</span>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{module.title}</h4>
                            {module.description && (
                              <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{module.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end sm:text-right gap-3 sm:gap-4">
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                              {completedChapters}/{totalChapters}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-orange-600 whitespace-nowrap">{module.total_duration}</span>
                          </div>
                          <span className="text-base sm:text-lg font-bold text-blue-600">{progress}%</span>
                        </div>
                      </div>
                      <div className="w-full h-2 sm:h-3 bg-gray-200 rounded-full">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${getProgressColor(progress)}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      {module.has_videos && (
                        <p className="text-xs text-green-600 mt-1">⚡ Duración calculada automáticamente</p>
                      )}
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 sm:py-12">
                    <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm sm:text-base">No hay módulos disponibles</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1 px-4">
                      Contacta al administrador para obtener acceso a los módulos
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Progress Summary */}
          <div className="space-y-4 sm:space-y-6">
            <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 ${contentLoaded ? 'animate-scaleIn stagger-4' : 'opacity-0'}`}>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                <span>Resumen de Progreso</span>
              </h3>
              
              {/* Overall Progress Circle */}
              <div className="text-center mb-4 sm:mb-6">
                <div className="relative inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24">
                  <svg className="w-20 h-20 sm:w-24 sm:h-24 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="transparent"
                      className="text-gray-200 sm:hidden"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="transparent"
                      className="text-gray-200 hidden sm:block"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="transparent"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - userStats.overallProgress / 100)}`}
                      className="text-blue-600 transition-all duration-700 sm:hidden"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="transparent"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - userStats.overallProgress / 100)}`}
                      className="text-blue-600 transition-all duration-700 hidden sm:block"
                    />
                  </svg>
                  <span className="absolute text-lg sm:text-xl font-bold text-blue-600">
                    {userStats.overallProgress}%
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-2">Progreso Total</p>
              </div>

              {/* Detailed Stats */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Módulos completados</span>
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">
                    {userStats.completedModules} de {userStats.totalModules}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Capítulos completados</span>
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">
                    {userStats.completedChapters} de {userStats.totalChapters}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Tiempo de contenido</span>
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">
                    {formatWatchTime(userStats.totalWatchTime)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs sm:text-sm text-gray-600">Estado</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    userStats.overallProgress === 100 
                      ? 'bg-green-100 text-green-800' 
                      : userStats.overallProgress > 0 
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    {userStats.overallProgress === 100 
                      ? 'Completado' 
                      : userStats.overallProgress > 0 
                        ? 'En Progreso'
                        : 'Sin Comenzar'
                    }
                  </span>
                </div>
              </div>
              
              {/* Note about auto-calculation */}
              <div className="mt-4 p-2.5 sm:p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-800">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Los tiempos se calculan automáticamente desde los videos para mayor precisión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;